/**
 * `/set-password` — where an invited admin lands on first login and where a reset link ends up
 * (PRD §8, PLAN.md D-18). Saving revokes every other session, so a leaked invite link stops
 * working the moment the real owner chooses a password.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GrantonomyLogo } from "@/components/g-logo";
import { LegalContinueNotice } from "@/components/legal-links";
import { PublicLayout } from "@/components/public-layout";
import { api, ApiError } from "@/lib/api";
import { homePathFor, SESSION_QUERY_KEY, useSession } from "@/lib/session";
import { MIN_PASSWORD_LENGTH, type SessionResponse } from "@/shared/schemas/auth";

export function SetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading } = useSession();
  const [params] = useSearchParams();
  const fromReset = params.get("from") === "reset";
  const requireCurrent = Boolean(user && !user.passwordSetupRequired && !fromReset);

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const save = useMutation({
    mutationFn: (body: { password: string; currentPassword?: string }) =>
      api.post<SessionResponse>("/auth/set-password", body),
    onSuccess: (data) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, data);
      toast.success(t("setPassword.success"));
      navigate(data.user ? homePathFor(data.user) : "/", { replace: true });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.localizedMessage : t("errors.INTERNAL_ERROR")),
  });

  if (!isLoading && !user) return <Navigate to="/login" replace />;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      toast.error(t("setPassword.mismatch"));
      return;
    }
    save.mutate({
      password,
      ...(requireCurrent ? { currentPassword } : {}),
    });
  }

  return (
    <PublicLayout>
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <GrantonomyLogo />
          </div>
          <h1 className="font-display text-4xl">
            {t(fromReset ? "setPassword.resetTitle" : "setPassword.title")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t(fromReset ? "setPassword.resetSubtitle" : "setPassword.subtitle")}
          </p>

          <form
            onSubmit={submit}
            className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6 shadow-soft"
          >
            {requireCurrent ? (
              <div>
                <Label className="text-xs" htmlFor="current-password">
                  {t("setPassword.currentLabel")}
                </Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                />
              </div>
            ) : null}
            <div>
              <Label className="text-xs" htmlFor="new-password">
                {t("setPassword.label")}
              </Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {t("setPassword.hint", { min_length: MIN_PASSWORD_LENGTH })}
              </p>
            </div>
            <div>
              <Label className="text-xs" htmlFor="confirm-password">
                {t("setPassword.confirmLabel")}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={save.isPending}>
              {save.isPending ? t("setPassword.submitting") : t("setPassword.submit")}
            </Button>
            <LegalContinueNotice className="mt-3" />
          </form>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
